// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { ChangeLogEvent } from '../../../state_manager/data_models/change_log_model.ts'
import { ChangeLogContextTable, fetchChangeLog } from '../services/changeLogService.ts'

export const CHANGE_LOG_PAGE_SIZE = 25

/**
 * useChangeLog — the Change Log tab's paginated feed. Per-record state is
 * tab-local (no Redux), like useAccountTopics: each mount fetches page 1
 * fresh, loadMore() appends the next page.
 */
export const useChangeLog = (
  contextTable: ChangeLogContextTable,
  recordId: string | null | undefined
) => {
  const recordIdString = recordId ? String(recordId) : null

  const [events, setEvents] = useState<ChangeLogEvent[]>([])
  const [count, setCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pageRef = useRef(1)

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const load = useCallback(async () => {
    if (!recordIdString) {
      setEvents([])
      setCount(0)
      setHasMore(false)
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchChangeLog(contextTable, recordIdString, 1, CHANGE_LOG_PAGE_SIZE, token)
      if (cancelledRef.current) return
      if (res.error) setError(res.error)
      pageRef.current = 1
      setEvents(res.data)
      setCount(res.count)
      setHasMore(res.hasMore)
    } catch (e) {
      if (!cancelledRef.current) {
        setError(e instanceof Error ? e.message : 'Failed to load change log')
      }
    } finally {
      if (!cancelledRef.current) setLoading(false)
    }
  }, [contextTable, recordIdString])

  useEffect(() => {
    load()
  }, [load])

  const loadMore = useCallback(async () => {
    if (!recordIdString || loadingMore || !hasMore) return
    setLoadingMore(true)
    setError(null)
    try {
      const token = getAccessToken(store.getState().auth)
      const nextPage = pageRef.current + 1
      const res = await fetchChangeLog(contextTable, recordIdString, nextPage, CHANGE_LOG_PAGE_SIZE, token)
      if (cancelledRef.current) return
      if (res.error) {
        setError(res.error)
        return
      }
      pageRef.current = nextPage
      setEvents(prev => [...prev, ...res.data])
      setCount(res.count)
      setHasMore(res.hasMore)
    } catch (e) {
      if (!cancelledRef.current) {
        setError(e instanceof Error ? e.message : 'Failed to load more change log entries')
      }
    } finally {
      if (!cancelledRef.current) setLoadingMore(false)
    }
  }, [contextTable, recordIdString, loadingMore, hasMore])

  return { events, count, hasMore, loading, loadingMore, error, loadMore, refetch: load }
}
